/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { DisplayFromFHIRAnnotation, displayStringFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import { DateTimeEntry, getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import BooleanEntry, { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import { orderedByValueSet } from "../dictionaries/THOandFHIRValueSets";
import { emptyReasonCodeableConceptSet, emptyReasonValueSet } from "../dictionaries/CodeSystemLookup";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";

const generateNarrativeForListResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p>";
        }
        if (resource.status) {
            innerDiv += "<p><b>Status: </b>" + resource.status + "</p>";
        }
        if (resource.date) {
            innerDiv += "<p><b>Date: </b>" + getDateTimeDisplay(resource.date) + "</p>";
        }
        if (resource.mode) {
            innerDiv += "<p><b>Mode: </b>" + resource.mode + "</p>";
        }
        if (resource.code) {
            innerDiv += "<p><b>Purpose: </b>" + displayStringFromFHIRCodeableConcept(resource.code) + "</p>";
        }
        if (resource.subject?.length) {
            for (const subject of resource.subject) {
                if (subject) {
                    innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(subject) + "</p>";
                }
            }
        }
        if (resource.encounter) {
            innerDiv += "<p><b>Encounter: </b>" + displayStringFromFHIRReference(resource.encounter) + "</p>";
        }
        if (resource.source) {
            innerDiv += "<p><b>Author/Creator: </b>" + displayStringFromFHIRReference(resource.source) + "</p>";
        }
        if (resource.contributor?.length > 0) {
            for (const contributor of resource.contributor) {
                innerDiv += "<p><b>Author/Creator: </b>" + displayStringFromFHIRReference(contributor) + "</p>";
            }
        }
        if (resource.orderedBy) {
            innerDiv += "<p><b>Ordered by: </b>" + simpleDisplayStringFromFHIRCodeableConcept(resource.orderedBy) + "</p>";
        }
        if (resource.note?.length) {
            for (const note of resource.note) {
                if (note) {
                    innerDiv += "<p><b>Note: </b>" + displayStringFromFHIRAnnotation(note) + "</p>";
                }
            }
        }
        if (resource.entry) {
            innerDiv += "<table><tr><th>Flag</th><th>Date</th><th>Item</th></tr>";
            for (const entry of resource.entry) {
                let flag = simpleDisplayStringFromFHIRCodeableConcept(entry.flag) || "";
                let itemDeleted = entry.deleted ? "DELETED" : "";
                let date = getDateTimeDisplay(entry.date) || "";
                let item = displayStringFromFHIRReference(entry.item) || "";

                innerDiv += "<tr><td>" + flag + " " + itemDeleted + "</td><td>" +
                    date + "</td><td>" + item + "</td></tr>";
            }
            innerDiv += "</table><br/>";
        }
        if (resource.emptyReason) {
            innerDiv += "<p><b>Why empty: </b>" + simpleDisplayStringFromFHIRCodeableConcept(resource.emptyReason) + "</p>";
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": '<div xmlns="http://www.w3.org/1999/xhtml">' + innerDiv + "</div>" };
}

const ListEntryEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed,
    referencedResourceTypes, startingResourceType, setProfile, selectProfile, containedResourceDictionary = {} }) => {

    const [listEntryState, setListEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingListEntry = {
            flag: "", deleted: "", date: "", item: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingListEntry.extension = startingValue.extension; }
            if (startingValue.flag) { startingListEntry.flag = startingValue.flag; }
            if (typeof startingValue.deleted === "boolean") { startingListEntry.deleted = startingValue.deleted; }
            if (startingValue.date) { startingListEntry.date = startingValue.date; }
            if (startingValue.item) { startingListEntry.item = startingValue.item; }
        }
        setListEntryState(JSON.parse(JSON.stringify(startingListEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(listEntryState).length > 0) {
            let newListEntry = {};
            if (listEntryState.extension) { newListEntry.extension = listEntryState.extension; }
            if (listEntryState.flag && Object.keys(listEntryState.flag).length > 0) {
                newListEntry.flag = listEntryState.flag;
            }
            if (typeof listEntryState.deleted === "boolean") { newListEntry.deleted = listEntryState.deleted; }
            if (listEntryState.date) { newListEntry.date = listEntryState.date; }
            if (listEntryState.item && Object.keys(listEntryState.item).length > 0) {
                newListEntry.item = listEntryState.item;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newListEntry } });
        }
    }), [listEntryState]);

    if (startCollapsedState) {
        return <div>
            <p><b>{fieldLabel}: </b></p>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.flag &&
                    <div>
                        <b>Flag: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.flag} />
                        <br /><br />
                    </div>}
                {startingValue.item &&
                    <div><DisplayFromFHIRReference reference={startingValue.item}
                        containedResourceJson={startingValue.item.reference &&
                            containedResourceDictionary[startingValue.item.reference]} /><br /></div>}
                {(typeof startingValue.deleted === "boolean") &&
                    <p><b>Deleted: </b>{displayStringFromFHIRBoolean(startingValue.deleted)}</p>}
                {startingValue.date &&
                    <p><b>Date added: </b>{getDateTimeDisplay(startingValue.date)}</p>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(listEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='flag' fieldLabel='Flag'
                    startingValue={listEntryState.flag} startCollapsed
                    setResourceState={setListEntryState} />
                <BooleanEntry elementName='deleted' fieldLabel='Deleted'
                    startingValue={listEntryState.deleted} setResourceState={setListEntryState} />
                <DateTimeEntry elementName='date' fieldLabel='Date added'
                    startingValue={listEntryState.date} setResourceState={setListEntryState} />
                <ReferenceEntry elementName='item' fieldLabel='The Item'
                    startingValue={listEntryState.item} enableCreation={true} startCollapsed
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={referencedResourceTypes}
                    startingResourceType={startingResourceType}
                    setProfile={setProfile}
                    selectProfile={selectProfile}
                    setResourceState={setListEntryState} />
            </div>
        </>
    }
}

const ListDisplay = ({ foi }) => {
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const [listResourceState, setListResourceState] = useState({});
    const [listReferencesState, setListReferencesState] = useState({ "references": [], "loaded": false });
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    let description;
    if (resourceJson.extension) {
        for (const extension of resourceJson.extension) {
            if (extension.url === "http://hl7.org/fhir/StructureDefinition/artifact-description" &&
                extension.valueMarkdown
            ) {
                description = extension.valueMarkdown;
            }
        }
    }

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Summary", "Description", "List Entries"]));
        dispatch(changeBrowserTabTitle("FEvIR List - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setListResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setListResourceState(resourceJson);
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            if (!listReferencesState?.loaded) {
                let listReferences = [];
                if (resourceJson?.entry) {
                    listReferences = resourceJson.entry.map(entry => { return entry.item }) || [];
                }
                let setProfile;
                let selectProfile;
                let referencedResourceTypes;
                let startingResourceType;
                if (resourceJson.code) {
                    let listTypeCode = resourceJson.code.coding?.[0]?.code || resourceJson.code.text;
                    if (listTypeCode === "BaselineVariablesList" || listTypeCode === "Baseline Variables List") {
                        setProfile = "VariableDefinition";
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "EvidenceList" || listTypeCode === "Evidence List") {
                        startingResourceType = "Evidence";
                        referencedResourceTypes = ["Evidence"];
                    }
                    if (listTypeCode === "NetEffectContributionList" || listTypeCode === "Net Effect Contributions List") {
                        setProfile = "NetEffectContribution";
                        startingResourceType = "Evidence";
                        referencedResourceTypes = ["Evidence"];
                    }
                    if (listTypeCode === "OutcomeList" || listTypeCode === "Outcome List") {
                        referencedResourceTypes = ["Group", "EvidenceVariable"];
                    }
                    if (listTypeCode === "OutcomeVariablesList" || listTypeCode === "Outcome Variables List") {
                        setProfile = "VariableDefinition";
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "ParticipantFlowVariablesList" || listTypeCode === "Participant Flow Variables List") {
                        selectProfile = ["ParticipantFlowEvidenceVariable", "ParticipantFlowReasonEvidenceVariable"];
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                    }
                    if (listTypeCode === "VariablesList" || listTypeCode === "Variables List") {
                        startingResourceType = "EvidenceVariable";
                        referencedResourceTypes = ["EvidenceVariable"];
                        selectProfile = ["VariableDefinition", "ParticipantFlowEvidenceVariable", "ParticipantFlowReasonEvidenceVariable"];
                    }
                }
                setListReferencesState({
                    "references": listReferences, "loaded": true,
                    "setProfile": setProfile, "selectProfile": selectProfile,
                    "startingResourceType": startingResourceType,
                    "referencedResourceTypes": referencedResourceTypes
                });
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: List " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(listResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.title || null;
            newResourceDetail.revisedResourceJson.title = listResourceState.title;
            newResourceDetail.displayTitle = listResourceState.title || newResourceDetail.displayTitle;
            if (listResourceState.title !== undefined &&
                previousFinalValue !== listResourceState.title) {
                console.log("resourceUpdate for List.title");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [listResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "status", "code", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.status]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "mode", "code", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.mode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "code", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.code]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "subject", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.subject]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "encounter", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.encounter]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "date", "dateTime", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.date]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "source", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.source]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "contributor", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.contributor]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "orderedBy", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.orderedBy]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.note]);

    useEffect(() => {
        if (listReferencesState?.references?.length) {
            let listEntries = listReferencesState.references.map(entry => { return { "item": entry } });
            setListResourceState(prevState => { return { ...prevState, entry: listEntries } });
        }
    }, [listReferencesState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(listResourceState, "entry", "ArrayOfListEntry", parentResourceDetail,
                dispatch);
        }
    }, [listResourceState.entry]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        <>{listResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <NarrativeEntry elementName="text"
                            startingValue={listResourceState.text}
                            generatedNarrative={generateNarrativeForListResource(listResourceState)}
                            setResourceState={setListResourceState} />
                        :
                        <>{
                            (listResourceState?.text?.div &&
                                listResourceState.text.status &&
                                listResourceState.text.status !== "empty")
                                ?
                                <DisplayFromFHIRXhtml xhtml={listResourceState.text.div} />
                                :
                                <p>[Narrative summary not generated.]</p>
                        }</>
                    }
                </div>
                <h3 id="description">Description</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <StringEntry elementName="title" fieldLabel="Title"
                            startingValue={listResourceState.title}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.title && <p><b>Title: </b>{listResourceState.title}</p>}</>
                    }
                    {description && <div>
                        <b>Description: </b><DisplayFromFHIRMarkdown markdown={description} />
                        <br /><br />
                    </div>}
                    {editMode ?
                        <CodeEntry elementName='status' fieldLabel='Status'
                            allowedValues={['current', 'retired', 'entered-in-error']}
                            startingValue={listResourceState.status}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.status && <p><b>Status: </b>{listResourceState.status}</p>}</>
                    }
                    {editMode ?
                        <CodeEntry elementName='mode' fieldLabel='Mode'
                            allowedValues={['working', 'snapshot', 'changes']}
                            startingValue={listResourceState.mode}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.mode && <p><b>Mode: </b>{listResourceState.mode}</p>}</>
                    }
                    {editMode ?
                        <CodeableConceptEntry elementName='code' fieldLabel='Purpose'
                            startCollapsed startingValue={listResourceState.code || null}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.code && <div>
                            <b>Purpose: </b><DisplayFromFHIRCodeableConcept codeableConcept={listResourceState.code} />
                            <br /><br />
                        </div>}</>
                    }

                    {editMode ?
                        <ArrayEntry datatype='Reference' elementName='subject' fieldLabel='Subject'
                            enableCreation={true} startCollapsed
                            startingValue={listResourceState.subject}
                            setResourceState={setListResourceState} />
                        :
                        <>
                            {(listResourceState.subject?.length === 1) &&
                                <p><b>Subject: </b><DisplayFromFHIRReference reference={listResourceState.subject[0]}
                                    containedResourceJson={listResourceState.subject[0]?.reference &&
                                        containedResourceDictionary[listResourceState.subject[0].reference]} /></p>}
                            {(listResourceState.subject?.length > 1) && listResourceState.subject.map((subject, subjectIndex) => {
                                return <div key={subjectIndex}>
                                    <div><b>Subject: </b><DisplayFromFHIRReference reference={subject}
                                        containedResourceJson={subject?.reference &&
                                            containedResourceDictionary[subject.reference]} /></div>
                                </div>
                            })}
                        </>
                    }
                    {editMode ?
                        <ReferenceEntry elementName='encounter' fieldLabel='Encounter'
                            containedResourceDictionary={containedResourceDictionary}
                            enableCreation={true} referencedResourceTypes={['Encounter']} startCollapsed
                            startingValue={listResourceState.encounter}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.encounter &&
                            <p><b>Encounter: </b><DisplayFromFHIRReference reference={listResourceState.encounter}
                                containedResourceJson={listResourceState.encounter.reference &&
                                    containedResourceDictionary[listResourceState.encounter.reference]} /></p>}</>
                    }
                    {editMode ?
                        <DateTimeEntry elementName='date' fieldLabel='Date'
                            startingValue={listResourceState.date}
                            setResourceState={setListResourceState} />
                        :
                        <>{listResourceState.date && <p><b>Date: </b>{getDateTimeDisplay(listResourceState.date)}</p>}</>
                    }
                    {editMode ?
                        <ReferenceEntry elementName='source' fieldLabel='Source (Author)'
                            containedResourceDictionary={containedResourceDictionary}
                            enableCreation={true} startCollapsed
                            referencedResourceTypes={['Practitioner', 'PractitionerRole', 'Patient', 'Device', 'Organization', 'RelatedPerson', 'CareTeam']}
                            startingValue={listResourceState.source}
                            setResourceState={setListResourceState} />
                        :
                        <>
                            {listResourceState.source &&
                                <p><b>Source (Author): </b><DisplayFromFHIRReference reference={listResourceState.source}
                                    containedResourceJson={listResourceState.source.reference &&
                                        containedResourceDictionary[listResourceState.source.reference]} /></p>}
                        </>
                    }
                    {editMode ?
                        <ArrayEntry datatype='Reference'
                            elementName='contributor' fieldLabel='Additional Contributor (Author)'
                            enableCreation={true} startCollapsed startEmptyArrayClosed={true}
                            deletable={true} deletableArray={true}
                            referencedResourceTypes={['Practitioner', 'PractitionerRole', 'Patient', 'Device', 'Organization', 'RelatedPerson', 'CareTeam']}
                            startingValue={listResourceState.contributor}
                            setResourceState={setListResourceState} />
                        :
                        <>
                            {listResourceState.contributor?.length > 0 &&
                                listResourceState.contributor.map((contributor, contributorIndex) => {
                                    return <p key={contributorIndex}><b>Source (Additional Contributor): </b><DisplayFromFHIRReference reference={contributor}
                                        containedResourceJson={contributor?.reference &&
                                            containedResourceDictionary[contributor.reference]} /></p>
                                })
                            }
                        </>
                    }
                    {editMode ?
                        <CodeableConceptEntry elementName='orderedBy'
                            fieldLabel='Ordered by' startingValue={listResourceState.orderedBy || null}
                            valueSet={orderedByValueSet} startCollapsed
                            setResourceState={setListResourceState} />
                        :
                        <>
                            {listResourceState.orderedBy && <div>
                                <b>Ordered by: </b><DisplayFromFHIRCodeableConcept codeableConcept={listResourceState.orderedBy} />
                                <br /><br />
                            </div>}
                        </>
                    }
                    {editMode ?
                        <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                            startingValue={listResourceState.note || null} startCollapsed
                            setResourceState={setListResourceState} />
                        :
                        <>
                            {(listResourceState.note?.length === 1) &&
                                <div><b>Note: </b><DisplayFromFHIRAnnotation annotation={listResourceState.note[0]} /></div>}
                            {(listResourceState.note?.length > 1) && listResourceState.note.map((note, noteIndex) => {
                                return <div key={noteIndex}>
                                    <div><b>Note: </b><DisplayFromFHIRAnnotation annotation={note} /></div>
                                </div>
                            })}
                        </>
                    }
                </div>
                <h3 id="list-entries">List Entries</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            {listReferencesState?.loaded && <>
                                <p>To manage the list entries as a simple set of resource references:</p>
                                <div style={{ marginLeft: "24px" }}>
                                    <ArrayEntry datatype='Reference' elementName='references'
                                        fieldLabel={(listReferencesState.setProfile ||
                                            listReferencesState.startingResourceType ||
                                            'The') + ' Resource'}
                                        startingValue={listReferencesState.references || []}
                                        referencedResourceTypes={listReferencesState.referencedResourceTypes}
                                        startingResourceType={listReferencesState.startingResourceType}
                                        setProfile={listReferencesState.setProfile}
                                        selectProfile={listReferencesState.selectProfile}
                                        startCollapsed startEmptyArrayClosed enableCreation={true}
                                        setResourceState={setListReferencesState} />
                                </div>
                            </>}
                            <ArrayEntry datatype='ListEntry' elementName='entry' startCollapsed
                                containedResourceDictionary={containedResourceDictionary}
                                fieldLabel='Entry' startingValue={listResourceState.entry}
                                referencedResourceTypes={listReferencesState.referencedResourceTypes}
                                startingResourceType={listReferencesState.startingResourceType}
                                setProfile={listReferencesState.setProfile}
                                selectProfile={listReferencesState.selectProfile}
                                setResourceState={setListResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(listResourceState.entry) && listResourceState.entry.map((entry, entryIndex) => {
                                return <div key={entryIndex}>
                                    <div><b>Entry {entryIndex + 1}: </b>
                                        {(entry.item && !entry.flag && !entry.date && (typeof entry.deleted !== "boolean")) ?
                                            <DisplayFromFHIRReference reference={entry.item}
                                                containedResourceJson={entry.item.reference &&
                                                    containedResourceDictionary[entry.item.reference]} />
                                            :
                                            <div style={{ marginLeft: "24px" }}>
                                                {entry.flag && <div><b>Flag: </b><DisplayFromFHIRCodeableConcept codeableConcept={entry.flag} />
                                                    <br /><br /></div>}
                                                {entry.item && <p><DisplayFromFHIRReference reference={entry.item}
                                                    containedResourceJson={entry.item.reference &&
                                                        containedResourceDictionary[entry.item.reference]} /></p>}
                                                {(typeof entry.deleted === "boolean") && <p><b>Deleted: </b>{displayStringFromFHIRBoolean(entry.deleted)}</p>}
                                                {entry.date && <p><b>Date added: </b>{getDateTimeDisplay(entry.date)}</p>}
                                            </div>
                                        }
                                    </div>
                                </div>
                            })}
                        </>
                    }
                    {editMode ?
                        <>{
                            (!listResourceState.entry || listResourceState.entry.length === 0) && <>
                                <br />
                                <p><b>Explain why this List is empty</b></p>
                                <CodeableConceptEntry elementName='emptyReason'
                                    fieldLabel='Empty Reason' startingValue={listResourceState.emptyReason || null}
                                    valueSet={emptyReasonValueSet} startCollapsed
                                    codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                                    setResourceState={setListResourceState} />
                            </>
                        }</>
                        :
                        <>{
                            ((!listResourceState.entry || listResourceState.entry.length === 0) &&
                                listResourceState.emptyReason) &&
                            <span><b>Why Empty: </b><DisplayFromFHIRCodeableConcept
                                codeableConcept={listResourceState.emptyReason} /></span>
                        }</>
                    }
                </div>
            </div>
        </>}</>
    </div>
}

export default ListDisplay;
export { ListEntryEntry };